import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useHistory, useRouteMatch } from 'react-router-dom'
import { CircularProgress, Stack } from '@mui/material'
import Grid from '@mui/material/GridLegacy'

import {
  Edit,
  RadioButtonChecked,
  ThumbsUpDown,
  CheckBox,
  RadioButtonUnchecked,
  CheckBoxOutlineBlankRounded,
  CheckBoxRounded,
  Shuffle,
  LowPriority,
  TextFields,
} from '@mui/icons-material'

import { roundNumber, useStores } from '@trivie/core'
import { QuestionApi } from '@trivie/core/src/services/api-objects'

import { color } from '@trivie/ui-web/theme'
import { DataTable } from '@trivie/ui-web/components/data-table'
import { Header } from '@trivie/ui-web/components/header'
import { Insight } from '@trivie/ui-web/components/insight'
import { Text } from '@trivie/ui-web/components/text'
import { ShortList } from '@trivie/ui-web/components/short-list'
import { Panel } from '@trivie/ui-web/components/panel/panel'
import { Switch } from '@trivie/ui-web/components/switch/switch'

export const QuestionDetailsScreen = observer(() => {
  const { questionStore, contentCreationStore, manageUserStore } = useStores()
  const history = useHistory()

  const { resetContentStore } = contentCreationStore
  const {
    questionStats,
    getQuestionDetails,
    getKnowledgeGain,
    getRelatedQuizzes,
    getRelatedTopicsForQuestion,
    knowledgeGain,
    relatedQuizzes,
    relatedTopics,
  } = questionStore
  const { permissions } = manageUserStore

  const { params } = useRouteMatch()
  const { id } = params as any

  const [loadingQuestion, setLoadingQuestion] = useState(true)
  const [loadingKG, setLoadingKG] = useState(true)
  const [loading, setLoading] = useState(true)

  const [answerCount, setAnswerCount] = useState(0)
  const [pctCorrect, setPctCorrect] = useState(0)
  const [answerTime, setAnswerTime] = useState(0)

  useEffect(() => {
    Promise.all([
      getRelatedQuizzes(id),
      getRelatedTopicsForQuestion(id),
      QuestionApi.getAllQuestions(
        'questions',
        false,
        [
          { field: 'question_answer_count' },
          { field: 'question_pct_correct' },
          { field: 'question_answer_time' },
        ],
        'list_questions',
        undefined,
        Number(id),
      ).then((resp) => {
        if (resp.ok) {
          const question = resp.data.rows[0]
          setAnswerTime(
            roundNumber(
              question.columns.find((column) => column.field === 'question_answer_time')?.value /
                1000 || 0,
              1,
            ),
          )
          setPctCorrect(
            roundNumber(
              question.columns.find((column) => column.field === 'question_pct_correct')?.value *
                100,
              1,
            ),
          )
          setAnswerCount(
            question.columns.find((column) => column.field === 'question_answer_count')?.value,
          )
        }
      }),
      getQuestionDetails(id).then(() => setLoadingQuestion(false)),
      getKnowledgeGain(id).then(() => setLoadingKG(false)),
    ]).finally(() => setLoading(false))
  }, [])

  const primary = {
    disabled: !permissions?.permissions.some((p) => p.code === 'can_edit_question'),
    startIcon: <Edit />,
    onClick: () => {
      resetContentStore()
      history.push(`/content/question/${(params as any).id}/edit`)
    },
    text: 'Edit Question',
  }

  const total = questionStats?.answers_summary.reduce(
    (acc: number, curr: any) => acc + roundNumber(curr.pct_selected * 100, 1),
    0,
  )
  const remaining = answerCount === 0 ? 0 : 100 - total <= 0 ? 0 : 100 - total
  const showRemaining = ['Ordering', 'Matching', 'Open Text'].includes(questionStats?.question_type)

  const renderQuestionType = (question_type: string) => {
    let qType
    switch (question_type) {
      case 'Multiple Choice':
        return (
          <>
            <RadioButtonChecked
              sx={{ marginRight: '12px' }}
              htmlColor={color.shade70}
              fontSize="small"
            />
            <Text
              style={{ color: color.shade70 }}
              variant="body2Emphasized"
              text="Multiple Choice"
            />
          </>
        )
      case 'True or False':
        return (
          <>
            <ThumbsUpDown sx={{ marginRight: '12px' }} htmlColor={color.shade70} fontSize="small" />
            <Text style={{ color: color.shade70 }} variant="body2Emphasized" text="True / False" />
          </>
        )
      case 'Multiple Response':
        return (
          <>
            <CheckBox sx={{ marginRight: '12px' }} htmlColor={color.shade70} fontSize="small" />
            <Text style={{ color: color.shade70 }} variant="body2Emphasized" text="Checkbox" />
          </>
        )
      case 'Matching':
        return (
          <>
            <Shuffle sx={{ marginRight: '12px' }} htmlColor={color.shade70} fontSize="small" />
            <Text style={{ color: color.shade70 }} variant="body2Emphasized" text="Matching" />
          </>
        )
      case 'Ordering':
        return (
          <>
            <LowPriority sx={{ marginRight: '12px' }} htmlColor={color.shade70} fontSize="small" />
            <Text style={{ color: color.shade70 }} variant="body2Emphasized" text="Ordering" />
          </>
        )
      case 'Open Text':
        return (
          <>
            <TextFields sx={{ marginRight: '12px' }} htmlColor={color.shade70} fontSize="small" />
            <Text style={{ color: color.shade70 }} variant="body2Emphasized" text="Open Text" />
          </>
        )
      default:
        return null
    }
  }

  return loading ? (
    <div style={{ margin: 'auto' }}>
      <CircularProgress />
    </div>
  ) : (
    <>
      <Header divider title="Question Details" primaryAction={primary} />
      <Text component="h2" mb={2} text="Statistics" variant="labelEmphasized" />
      {!loadingKG && (
        <Grid container spacing={2} mb={4}>
          <Grid item lg={4} md={6} sm={12}>
            <Panel fill>
              <Insight
                tooltip="The number of times the question has been answered"
                title="Answer Count"
                value={answerCount}
              />
            </Panel>
          </Grid>
          <Grid item lg={4} md={6} sm={12}>
            <Panel fill>
              <Insight
                tooltip="Percentage of all answers that were answered correctly"
                title="Correct %"
                value={`${pctCorrect}%`}
              />
            </Panel>
          </Grid>
          {/* <Grid item lg={3} md={6} sm={12}>
            <Panel fill>
              <Insight
                tooltip="Tooltip"
                noAveraging
                unit="%"
                title="Unique Respondents"
                value={answerCount}
              />
            </Panel>
          </Grid> */}
          <Grid item lg={4} md={6} sm={12}>
            <Panel fill>
              <Insight
                tooltip="Average time in seconds it takes users to answer the question"
                title="Average Answer Time"
                value={answerTime ? `${answerTime} secs` : '-'}
              />
            </Panel>
          </Grid>
          <Grid item lg={4} md={6} sm={12}>
            <Panel fill>
              <Insight
                tooltip="Starting knowledge for this question"
                title="Starting Knowledge"
                value={
                  knowledgeGain ? `${Math.round(knowledgeGain.starting_knowledge) ?? 0}%` : '-'
                }
              />
            </Panel>
          </Grid>
          <Grid item lg={4} md={6} sm={12}>
            <Panel fill>
              <Insight
                tooltip="Current knowledge gain for this question"
                title="Current Knowledge"
                value={knowledgeGain ? `${Math.round(knowledgeGain.current_knowledge) ?? 0}%` : '-'}
              />
            </Panel>
          </Grid>
          <Grid item lg={4} md={6} sm={126}>
            <Panel fill>
              <Insight
                tooltip="Average difference between the first and most recent answers for the question"
                title="Knowledge Gain"
                value={knowledgeGain ? `${Math.round(knowledgeGain.knowledge_gain) ?? 0}%` : '-'}
              />
            </Panel>
          </Grid>
        </Grid>
      )}
      {!loadingQuestion && (
        <>
          <Text component="h2" mb={2} text="Question" variant="labelEmphasized" />
          <Panel sx={{ mb: 4 }}>
            <Stack useFlexGap gap={2}>
              <Stack
                pb={1}
                sx={{ borderBottom: `1px solid ${color.shade10}` }}
                direction="row"
                alignItems="center"
              >
                {renderQuestionType(questionStats.question_type)}
              </Stack>
              {questionStats?.image_url && (
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 28 }}>
                  <img src={questionStats?.image_url} style={IMAGE} />
                </div>
              )}

              <Text
                component="div"
                pb={1}
                text={questionStats?.text}
                variant="labelEmphasized"
                sx={{ borderBottom: `1px solid ${color.shade10}` }}
              />
              <DataTable
                disableSearch
                hideFooter
                getRowHeight={({ id, densityFactor }) => {
                  return 'auto'
                }}
                checkboxSelection={false}
                sx={{
                  border: 'none',
                  '& .MuiDataGrid-columnHeaders > div': {
                    borderTopLeftRadius: '20px !important',
                    borderTopRightRadius: '20px !important',
                  },
                  '& .MuiDataGrid-cell': {
                    display: 'flex',
                  },
                  '& .MuiDataGrid-row': {
                    minHeight: '68px !important',
                  },
                }}
                columns={[
                  {
                    field: 'correct',
                    headerName: ' ',
                    width: 40,
                    renderCell: (p: any) => {
                      return (
                        <div
                          style={{
                            height: '100%',
                            justifyContent: 'center',
                            display: 'flex',
                            width: '100%',
                          }}
                        >
                          {questionStats?.question_type === 'Multiple Response' && p.row.correct ? (
                            <CheckBoxRounded style={CORRECT_ICON} />
                          ) : questionStats?.question_type === 'Multiple Response' &&
                            !p.row.correct ? (
                            <CheckBoxOutlineBlankRounded style={CORRECT_ICON} />
                          ) : p.row.correct ? (
                            <RadioButtonChecked style={CORRECT_ICON} />
                          ) : (
                            <RadioButtonUnchecked style={CORRECT_ICON} />
                          )}
                        </div>
                      )
                    },
                  },
                  {
                    field: 'text',
                    headerName: 'ANSWER',
                    flex: 1,
                    type: 'string',
                    renderCell: (p: any) => {
                      if (p.row.pairs?.length) {
                        return (
                          <Stack direction="column" gap="12px" paddingY="12px">
                            {p.row.pairs.map((pair) => {
                              return (
                                <Text component="div">
                                  {pair.left}
                                  <span style={{ fontWeight: 600 }}>&nbsp;&nbsp;→&nbsp;&nbsp;</span>
                                  {pair.right}
                                </Text>
                              )
                            })}
                          </Stack>
                        )
                      } else if (p.row.ordering?.length) {
                        return (
                          <Stack direction="column" gap="12px" paddingY="12px">
                            {p.row.ordering.map((order, idx) => {
                              return (
                                <div style={{ display: 'flex', gap: 8, flexDirection: 'row' }}>
                                  <Text
                                    style={{ fontWeight: 600 }}
                                    text={`${idx + 1}.`}
                                    component="div"
                                  />
                                  <Text text={order.text} component="div" />
                                </div>
                              )
                            })}
                          </Stack>
                        )
                      } else {
                        return (
                          <Stack direction="column" gap="12px" paddingY="12px">
                            <Text text={p.row.text} component="div" />
                          </Stack>
                        )
                      }
                    },
                  },
                  {
                    field: 'pct_selected',
                    headerName: '% SELECTED',
                    width: 150,
                    type: 'number',
                    renderCell: (p: any) => `${roundNumber(p.row.pct_selected * 100, 1)}%`,
                  },
                  {
                    field: 'avg_time',
                    headerName: 'AVERAGE TIME',
                    width: 150,
                    type: 'number',
                    renderCell: (p: any) =>
                      p.row.avg_time ? `${roundNumber(p.row.avg_time / 1000, 1)} secs` : 'N/A',
                  },
                ]}
                toolbar={null}
                onRowSelectionModelChange={(rowSelectionModel) => {}}
                hidePagination
                hideActions
                getRowId={(row) => row.choice_id}
                rows={
                  remaining && showRemaining
                    ? [
                        ...questionStats?.answers_summary,
                        {
                          text: 'Other',
                          pct_selected: remaining / 100,
                          choice_id: 0,
                        },
                      ]
                    : questionStats?.answers_summary ?? []
                }
              />
              {questionStats?.factoid?.text && (
                <Stack useFlexGap gap={1}>
                  <Text
                    component="div"
                    variant="body2Emphasized"
                    text="Feedback"
                    style={{ color: color.shade50 }}
                  />
                  <Text
                    component="div"
                    variant="body2"
                    text={questionStats?.factoid?.text}
                    pb={1}
                    sx={{ borderBottom: `1px solid ${color.shade10}` }}
                  />
                </Stack>
              )}
              <Stack useFlexGap gap={2}>
                <Text
                  component="div"
                  variant="body2Emphasized"
                  text="Settings"
                  style={{ color: color.shade50 }}
                />
                {questionStats?.question_type === 'Open Text' && (
                  <Stack direction="row" justifyContent="space-between">
                    <Stack useFlexGap gap={0.5}>
                      <Text variant="body2Emphasized" text="Case Sensitive" />
                      <Text
                        color="text.secondary"
                        variant="body2"
                        text="Require learners to match capitalization."
                      />
                    </Stack>
                    <Switch
                      color="success"
                      checked={Boolean(questionStats?.case_sensitive)}
                      disabled
                    />
                  </Stack>
                )}
                <Stack direction="row" justifyContent="space-between">
                  <Stack useFlexGap gap={0.5}>
                    <Text variant="body2Emphasized" text="Reinforcement Eligible" />
                    <Text
                      color="text.secondary"
                      variant="body2"
                      text="Include this question in reinforcement quizzes."
                    />
                  </Stack>
                  <Switch
                    color="success"
                    checked={Boolean(questionStats?.adaptive_eligible)}
                    disabled
                  />
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Stack useFlexGap gap={0.5}>
                    <Text variant="body2Emphasized" text="Shuffle Answer Order" />
                    <Text
                      color="text.secondary"
                      variant="body2"
                      text="Randomize the order of this question's answers."
                    />
                  </Stack>
                  <Switch
                    color="success"
                    checked={!Boolean(questionStats?.keep_answer_order)}
                    disabled
                  />
                </Stack>
                {questionStats.external_id && (
                  <Stack direction="row" justifyContent="space-between">
                    <Stack useFlexGap gap={0.5}>
                      <Text variant="body2Emphasized" text="External ID" />
                      <Text
                        color="text.secondary"
                        variant="body2"
                        text="Refer to this question outside of Trivie."
                      />
                    </Stack>
                    <Text
                      component="div"
                      text={questionStats.external_id}
                      variant="title"
                      sx={{
                        minWidth: 160,
                        lineHeight: '32px',
                        borderBottom: `1px solid ${color.shade10}`,
                      }}
                    />
                  </Stack>
                )}
              </Stack>
            </Stack>
          </Panel>
        </>
      )}
      <>
        <Text mb={2} component="h2" text="Memberships" variant="labelEmphasized" />
        <Grid container spacing={2}>
          <Grid item lg={6} md={6} sm={12}>
            <ShortList data={relatedQuizzes} type="Quizzes" />
          </Grid>
          <Grid item lg={6} md={6} sm={12}>
            <ShortList data={relatedTopics} type="Topics" />
          </Grid>
        </Grid>
      </>
    </>
  )
})

const CORRECT_ICON: React.CSSProperties = {
  alignSelf: 'center',
  left: 20,
  fontSize: 20,
}
const IMAGE: React.CSSProperties = {
  objectFit: 'contain',
  maxHeight: 400,
  maxWidth: 400,
  alignSelf: 'center',
}
